/**
 * Helpers de presentación del dominio de Inventario (formato de cantidades,
 * lotes e historial).
 *
 * Puro formato para la UI; sin lógica de negocio ni acceso a la API. Las fechas
 * llegan del backend ya formateadas (`InventarioService.data` usa `strftime`),
 * así que aquí solo se reexhiben tal cual.
 */
import { ApiError } from "@/lib/api";

import { INVENTARIO_ERROR_MESSAGE } from "./constants";
import type {
  AceroData,
  GalvData,
  HistorialEntry,
  RolloAcero,
  RolloGalv,
} from "./types";

/**
 * Formatea un peso en kg (como el SSR: `>= 1000` -> toneladas, si no kg con un
 * decimal). Ej.: 1500 -> "1.50 t"; 80 -> "80.0 kg".
 */
export function formatKg(kg: number): string {
  if (kg >= 1000) {
    return `${(kg / 1000).toFixed(2)} t`;
  }
  return `${kg.toFixed(1)} kg`;
}

/** Formatea metros lineales con un decimal (ej.: 50 -> "50.0 m"). */
export function formatMetros(metros: number): string {
  return `${metros.toFixed(1)} m`;
}

/** Total de lotes galv (nº de lotes en todos los calibres/formatos). */
export function totalLotesGalv(galv: GalvData): number {
  let total = 0;
  for (const formatos of Object.values(galv)) {
    total += formatos["4x8"].length + formatos["1x2"].length;
  }
  return total;
}

/** Total de kg disponibles en toda la galvanizada. */
export function totalKgGalv(galv: GalvData): number {
  let total = 0;
  for (const formatos of Object.values(galv)) {
    for (const lote of [...formatos["4x8"], ...formatos["1x2"]]) {
      total += lote.kg || 0;
    }
  }
  return total;
}

/** Total de lotes de acero/aluminio. */
export function totalLotesAcero(acero: AceroData): number {
  let total = 0;
  for (const lotes of Object.values(acero)) {
    total += lotes.length;
  }
  return total;
}

/** Total de metros disponibles en todo el acero/aluminio. */
export function totalMetrosAcero(acero: AceroData): number {
  let total = 0;
  for (const lotes of Object.values(acero)) {
    for (const lote of lotes) {
      total += lote.metros || 0;
    }
  }
  return total;
}

/**
 * Porcentaje disponible de un lote galv (kg actual / kg inicial), 0-100. Sin
 * `kg_orig` válido devuelve 100 (no se puede estimar; se muestra lleno).
 */
export function pctGalv(lote: RolloGalv): number {
  if (!lote.kg_orig || lote.kg_orig <= 0) {
    return 100;
  }
  return Math.round((lote.kg / lote.kg_orig) * 100);
}

/** Porcentaje disponible de un lote de acero (metros actual / metros inicial). */
export function pctAcero(lote: RolloAcero): number {
  if (!lote.metros_orig || lote.metros_orig <= 0) {
    return 100;
  }
  return Math.round((lote.metros / lote.metros_orig) * 100);
}

/**
 * Texto de la cantidad consumida en una entrada del historial. Las galv traen kg
 * (+ láminas estimadas); las acero, metros (+ kg aprox). Replica el desglose del
 * SSR sin reinterpretar la zona horaria ni los números.
 */
export function consumoEntryText(entry: HistorialEntry): string {
  if (entry.tipo === "galv") {
    const kg = entry.kg_consumido ?? 0;
    const lams = entry.lams ?? 0;
    return `${kg.toFixed(1)} kg · ${lams} lám`;
  }
  const metros = entry.metros_consumido ?? 0;
  const kgAprox = entry.kg_aprox ?? 0;
  return `${metros.toFixed(1)} m · ${kgAprox.toFixed(1)} kg aprox`;
}

/**
 * Traduce un error de la API a un mensaje legible para el usuario.
 *
 * El backend de inventario devuelve en `message` el texto humano del service
 * (p. ej. "Datos inválidos"), por lo que se prefiere ese mensaje; el
 * `INVENTARIO_ERROR_MESSAGE` (por `error_code`) solo es un respaldo cuando el
 * backend no trae mensaje. Lo comparten los diálogos y el formulario.
 */
export function inventarioErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return (
      error.message ||
      INVENTARIO_ERROR_MESSAGE[error.error_code] ||
      "No se pudo completar la acción."
    );
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "No se pudo completar la acción.";
}
